/**
 * MentionInput: textarea/input con autocompletado @ para mencionar miembros del espacio.
 *
 * Sintaxis interna:
 *   El usuario ve: "Hola @Juan Perez, ..."
 *   Se guarda el texto literal + array de menciones (IDs).
 *
 * Props:
 *   - value, onChange(text, menciones[]), spaceId, token, placeholder, rows, autoFocus, disabled
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function MentionInput({
  value = '',
  onChange,
  spaceId,
  token,
  placeholder = 'Escribí algo...',
  rows = 3,
  autoFocus = false,
  disabled = false,
  testId = 'mention-input',
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSugg, setShowSugg] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [mentionStart, setMentionStart] = useState(-1);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mencionesIds, setMencionesIds] = useState([]);
  const textareaRef = useRef(null);
  const suggesterTimeout = useRef(null);

  // Re-extraer menciones del texto (cuando cambia value desde afuera)
  useEffect(() => {
    // Mantener solo IDs que todavía están en el texto como @Nombre Apellido
    // Simplificación: no removemos IDs si el texto no contiene exactamente la mención;
    // el backend valida menciones contra membership igual.
  }, [value]);

  const fetchSuggestions = useCallback(async (q) => {
    clearTimeout(suggesterTimeout.current);
    suggesterTimeout.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API_URL}/campus/espacios/${spaceId}/buscar-miembros`, {
          params: { q, limit: 6 },
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuggestions(res.data || []);
        setActiveIdx(0);
      } catch (e) {
        setSuggestions([]);
      }
    }, 150);
  }, [spaceId, token]);

  const detectMention = (text, cursorPos) => {
    // Buscar el último '@' antes del cursor que no esté precedido por una letra/número (es decir, palabra nueva)
    const before = text.slice(0, cursorPos);
    const lastAt = before.lastIndexOf('@');
    if (lastAt === -1) return null;
    // Caracter anterior al @ debe ser inicio o espacio
    const prev = lastAt === 0 ? ' ' : before[lastAt - 1];
    if (!/[\s\n]/.test(prev)) return null;
    const query = before.slice(lastAt + 1);
    // Si hay un espacio o salto de línea, ya terminó la mención
    if (/[\s\n]/.test(query)) return null;
    return { start: lastAt, query };
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    const cursor = e.target.selectionStart;
    const mention = detectMention(newValue, cursor);
    if (mention) {
      setMentionStart(mention.start);
      setMentionQuery(mention.query);
      setShowSugg(true);
      fetchSuggestions(mention.query);
    } else {
      setShowSugg(false);
    }
    onChange?.(newValue, mencionesIds);
  };

  const pickSuggestion = (user) => {
    if (mentionStart < 0 || !textareaRef.current) return;
    const text = value;
    const cursor = textareaRef.current.selectionStart;
    const before = text.slice(0, mentionStart);
    const after = text.slice(cursor);
    const label = user.label || `${user.nombre || ''} ${user.apellido || ''}`.trim();
    const newText = `${before}@${label} ${after}`;
    const newMenciones = mencionesIds.includes(user.id) ? mencionesIds : [...mencionesIds, user.id];
    setMencionesIds(newMenciones);
    setShowSugg(false);
    onChange?.(newText, newMenciones);
    // Posicionar el cursor después de la mención
    setTimeout(() => {
      if (textareaRef.current) {
        const newCursorPos = before.length + 1 + label.length + 1;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (!showSugg || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => (i - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      pickSuggestion(suggestions[activeIdx]);
    } else if (e.key === 'Escape') {
      setShowSugg(false);
    }
  };

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={rows}
        autoFocus={autoFocus}
        disabled={disabled}
        className="w-full px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-y"
        data-testid={testId}
      />
      {showSugg && suggestions.length > 0 && (
        <div
          className="absolute z-30 left-0 right-0 mt-1 bg-white border rounded-md shadow-lg max-h-56 overflow-y-auto"
          data-testid="mention-suggestions"
        >
          {suggestions.map((s, idx) => (
            <button
              key={s.id}
              type="button"
              onClick={() => pickSuggestion(s)}
              className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition ${
                idx === activeIdx ? 'bg-violet-50' : 'hover:bg-gray-50'
              }`}
              data-testid={`mention-suggestion-${s.id}`}
            >
              <div className="w-7 h-7 bg-violet-100 rounded-full flex items-center justify-center text-violet-700 text-xs font-semibold">
                {(s.nombre?.[0] || '?').toUpperCase()}
              </div>
              <span>{s.label}</span>
            </button>
          ))}
        </div>
      )}
      {mencionesIds.length > 0 && (
        <p className="text-[10px] text-gray-400 mt-1" data-testid="mention-count">
          @ {mencionesIds.length} mencionado{mencionesIds.length !== 1 ? 's' : ''}
        </p>
      )}
    </div>
  );
}
